import { html, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Accordion, AccordionItem, actions, ComponentId, ComponentStore, dom, List, ListItemRenderer, MenuEvent, Overlay } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ToolbarItem } from "@omegagrid/toolbar";
import { FormCheckbox } from "@omegagrid/form";
import { ModalSidebarOptions } from "@omegagrid/bucket/dist/types";
import { AppComponent } from "../component";
import { formatDateTime } from "../formatters";
import { SyncEditorAdapter, SyncEditorOptions } from "./syncEditorAdapter";
import { ObjectShare } from "@reporting/rp-types/core";
import { style, itemStyle } from "./syncEditor.style";

const FIRST_COLUMN_WIDTH = 150;
@customElement('rp-sync-editor')
export class SyncEditor extends AppComponent {

	static styles = [style];

	@property({type: Object})
	adapter: SyncEditorAdapter;

	@state()
	groups: AccordionItem[];

	@state()
	toolbarItems: ToolbarItem[] = [];

	@query('og-accordion')
	accordion: Accordion;

	@query('og-overlay')
	loader: Overlay;

	private sharedObjects: ObjectShare[];
	private syncObjects: ObjectShare[];

	private options: SyncEditorOptions;

	showLoader = () => this.loader.show(this);
	hideLoader = () => this.loader.hide();

	async willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('adapter')) {
			this.options = await this.adapter.getOptions();
			this.toolbarItems = await this.createToolbarItems();
		}
	}

	async updated(props: Map<PropertyKey, unknown>) {
		if (props.has('adapter') && this.adapter) {
			await this.refresh();
		}
	}

	async createToolbarItems() {
		const options = this.options;
		if (!options.toolbar) return [];
		const items: ToolbarItem[] = [];

		if (options.toolbar.refresh?.enabled) items.push({
			type: 'button',
			id: 'refresh',
			text: options.toolbar.refresh.text,
			color: options.toolbar.refresh.color,
			icon: options.toolbar.refresh.icon
		});

		if (options.toolbar.new?.enabled) items.push({
			type: 'button',
			id: 'new',
			text: options.toolbar.new.text,
			color: options.toolbar.new.color,
			icon: options.toolbar.new.icon
		});

		if (options.toolbar.sync?.enabled) items.push({
			type: 'button',
			id: 'sync',
			text: options.toolbar.sync.text,
			color: options.toolbar.sync.color,
			icon: options.toolbar.sync.icon
		});
		return items;
	}

	components = new ComponentStore((id: ComponentId) => {
		const list = dom.createElement<List>('og-list');
		list.disabledSelect = true;
		list.customStyle = itemStyle;
		list.itemHeight = this.options.listItemFields.length * 20 + 20;
		const items = id == 'syncObjects' ? this.syncObjects : this.sharedObjects;
		list.itemRenderer = this.createListItemRenderer(items);
		list.size = items.length;
		return list;
	});

	createListItemRenderer(items: ObjectShare[]): ListItemRenderer {
		return (div: HTMLDivElement, index: number): string | TemplateResult<1> => {
			const menuItems = [
				{key: 'edit', value: msg('Edit'), icon: 'pen'},
				{key: 'delete', value: msg('Delete'), icon: 'trash', color: 'red'}
			];
			const o = items[index];
			const fields = this.options.listItemFields;

			return html`
				<div class="object-line-1">
					<div class="object-name">${o.name}</div>
					${this.options.selectableItems ? html`
						<div class="object-select">
							<og-form-checkbox id="object_share_${o.idz}" data-idz="${o.idz}"></og-form-checkbox>
						</div>
					` : ''}
					<div class="object-menu">
						<og-dropdown-menu
							data-idz="${o.idz}"
							id="menu"
							noCaret
							dynamicWidth
							alignment="right"
							color="transparent"
							style="flex: 0; min-width: 25px"
							.items="${menuItems}"
							@menu.select="${(e: MenuEvent) => this._onMenuSelect(e, o)}">
							<div slot="label"><og-icon .icon="${{icon: "ellipsis"}}"></og-icon></div>
						</og-dropdown-menu>
					</div>
				</div>

				<div class="object-line-2">
					<table>
						<tbody>
							${fields.includes('company') ? html`
								<tr>
									<td style="width: ${FIRST_COLUMN_WIDTH}px;">${msg('Target company')}</td>
									<td>${o.company}</td>
								</tr>` : ''}
							${fields.includes('name_share') ? html`
								<tr>
									<td>${msg('Target object')}</td>
									<td>${o.name_share}</td>
								</tr>` : ''}
							${fields.includes('date_created') ? html`
								<tr>
									<td>${msg('Created')}</td>
									<td>${formatDateTime(o.date_created)}</td>
								</tr>` : ''}
							${fields.includes('who_created') ? html`
								<tr>
									<td>${msg('Created by')}</td>
									<td>${o.who_created}</td>
								</tr>` : ''}
						</tbody>
					</table>
				</div>
			`;
		};
	}

	getAccordionItems() {
		const items: AccordionItem[] = [];
		if (this.syncObjects?.length > 0) items.push({id: 'syncObjects', title: msg('Synchronization'), icon: 'arrows-spin'});
		if (this.sharedObjects?.length > 0) items.push({id: 'sharedObjects', title: msg('Sharing'), icon: 'share-nodes'});
		return items;
	}

	async refresh() {
		this.showLoader();

		this.components.clear();
		const objects = await this.adapter.getObjects();
		this.syncObjects = objects.filter(o => !o.only_share);
		this.sharedObjects = objects.filter(o => o.only_share);

		this.requestUpdate();
		await this.updateComplete;
		this.accordion?.requestUpdate();
		this.hideLoader();
	}

	async edit(idz: number = null) {
		this.showLoader();
		const opts = {
			component: await this.adapter.getEditFormComponent(idz),
			position: 'left',
			width: 400,
		} as ModalSidebarOptions;
		(await this.host.openModalSidebar(opts)).addEventListener('form.save', () => this.refresh(), { once: true });
		this.hideLoader();
	}

	async delete(idz: number) {
		this.showLoader();
		await this.adapter.delete(idz);
		this.refresh();
	}

	async sync() {
		this.showLoader();
		try { await this.adapter.synchronize(this.getSelectedIds()); } catch (e) {}
		this.hideLoader();
	}

	getSelectedIds(): number[]|null {
		if (!this.options.selectableItems) return null;

		const selectedIds = Array.from(this.components.items)
			.flatMap(([_, list]) => list?.shadowRoot ? Array.from(list.shadowRoot.querySelectorAll('og-form-checkbox')) : [])
			.filter((checkbox: FormCheckbox) => checkbox.value === true)
			.map((checkbox: FormCheckbox) => parseInt(checkbox.getAttribute('data-idz')!));

		return selectedIds.length > 0 ? selectedIds : null;
	}

	async _onToolbarAction(e: actions.ActionEvent) {
		switch (e.action.key) {
			case 'refresh': this.refresh(); break;
			case 'new': this.edit(); break;
			case 'sync': this.sync(); break;
		}
	}

	_onMenuSelect = async (e: MenuEvent, o: ObjectShare) => {
		switch (e.item.key) {
			case 'edit':
				await this.edit(o.idz);
				break;
			case 'delete':
				if ((await this.host.openMenu({
					text: `${msg('Are you sure you want to delete')} '${o.name}'?`,
					content: 'yes_no'
				})).id == 'Y') {
					await this.delete(o.idz);
				}
				break;
		}
	}

	render = () => {
		const items = this.getAccordionItems();
		return html`
			<og-overlay defaultLoader></og-overlay>
			<div class="controls">
				<og-toolbar
					.items="${this.toolbarItems}"
					@toolbar.action="${(e: actions.ActionEvent) => this._onToolbarAction(e)}">
				</og-toolbar>
			</div>

			<div class="body">
				${items.length > 0 ? html`
					<og-accordion
						.createComponent="${(id: ComponentId) => this.components.get(id)}"
						.items="${items}">
					</og-accordion>
				` : html`<p class="empty">${msg('No items')}</p>`}
			</div>
		`;
	}
}
